/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { TaxRegimeSelector } from '../../../components/TaxRegimeSelector';
import {
  IIBB_PICKER_OPTIONS,
  iibbSelectionLabel,
} from '../../../domain/fiscalPresentation';
import type { IibbSelection } from '../../../domain/fiscalPresentation';
import type { TaxRegime } from '../../../domain/fiscalTypes';
import { ModalNestedPicker } from '../../../../shared/modals/ModalNestedPicker';
import { NumericTextInput } from '../../../../shared/ui/NumericTextInput';
import { InventoryIsoDateField } from '../../../../shared/ui/InventoryIsoDateField';
import { spacing } from '../../../../shared/theme/spacing';
import { typography } from '../../../../shared/theme/typography';
import { ticketColors } from '../../../../shared/theme/ticketColors';

export type ArcaSetupDataStepProps = {
  isWide: boolean;
  disabled: boolean;
  taxRegime: TaxRegime;
  monotributoCategory: string;
  onChangeRegime: (regime: TaxRegime) => void;
  onChangeCategory: (category: string) => void;
  emitterCuit: string;
  onChangeCuit: (value: string) => void;
  businessName: string;
  onChangeBusinessName: (value: string) => void;
  businessNameInputRef: React.RefObject<TextInput | null>;
  domicilioComercial: string;
  onChangeDomicilio: (value: string) => void;
  iibbSelection: IibbSelection;
  onChangeIibbSelection: (value: IibbSelection) => void;
  iibbNumero: string;
  onChangeIibbNumero: (value: string) => void;
  inicioActividades: string;
  onChangeInicio: (value: string) => void;
};

export function ArcaSetupDataStep({
  isWide,
  disabled,
  taxRegime,
  monotributoCategory,
  onChangeRegime,
  onChangeCategory,
  emitterCuit,
  onChangeCuit,
  businessName,
  onChangeBusinessName,
  businessNameInputRef,
  domicilioComercial,
  onChangeDomicilio,
  iibbSelection,
  onChangeIibbSelection,
  iibbNumero,
  onChangeIibbNumero,
  inicioActividades,
  onChangeInicio,
}: ArcaSetupDataStepProps) {
  const [iibbPickerVisible, setIibbPickerVisible] = useState(false);

  return (
    <View style={styles.card}>
      <Text style={styles.title}>Tus datos</Text>
      <Text style={styles.helper}>Van impresos en la factura.</Text>
      <TaxRegimeSelector
        value={taxRegime}
        monotributoCategory={monotributoCategory}
        onChangeRegime={onChangeRegime}
        onChangeCategory={onChangeCategory}
        disabled={disabled}
      />
      <View style={[styles.grid, isWide && styles.gridWide]}>
        <View style={styles.field}>
          <Text style={styles.label}>CUIT</Text>
          <NumericTextInput
            kind="digits"
            accessibilityLabel="CUIT emisor"
            style={styles.input}
            value={emitterCuit}
            onChangeText={onChangeCuit}
            placeholder="20XXXXXXXXX"
            placeholderTextColor={ticketColors.ink3}
            editable={!disabled}
          />
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>Razón social</Text>
          <TextInput
            ref={businessNameInputRef}
            accessibilityLabel="Razón social"
            style={styles.input}
            value={businessName}
            onChangeText={onChangeBusinessName}
            placeholder="Nombre del negocio"
            placeholderTextColor={ticketColors.ink3}
            editable={!disabled}
          />
        </View>
      </View>
      <View style={styles.field}>
        <Text style={styles.label}>Domicilio</Text>
        <TextInput
          accessibilityLabel="Domicilio comercial"
          style={styles.input}
          value={domicilioComercial}
          onChangeText={onChangeDomicilio}
          placeholder="Calle, número y ciudad"
          placeholderTextColor={ticketColors.ink3}
          editable={!disabled}
        />
      </View>
      <View style={[styles.grid, isWide && styles.gridWide]}>
        <View style={styles.field}>
          <Text style={styles.label}>Ingresos brutos</Text>
          <TouchableOpacity
            style={[styles.select, disabled && styles.disabled]}
            onPress={() => setIibbPickerVisible(true)}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel={`Ingresos brutos: ${iibbSelectionLabel(iibbSelection)}`}
            testID="fiscal.wizard.iibb"
          >
            <Text style={styles.selectValue}>{iibbSelectionLabel(iibbSelection)}</Text>
            <Ionicons name="chevron-down" size={16} color={ticketColors.ink3} />
          </TouchableOpacity>
          {iibbSelection === 'numero' ? (
            <NumericTextInput
              kind="digits"
              accessibilityLabel="Número de ingresos brutos"
              style={styles.input}
              value={iibbNumero}
              onChangeText={onChangeIibbNumero}
              placeholder="Número"
              placeholderTextColor={ticketColors.ink3}
              editable={!disabled}
            />
          ) : null}
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>Inicio de actividades</Text>
          <View pointerEvents={disabled ? 'none' : 'auto'} style={disabled ? styles.disabled : undefined}>
            <InventoryIsoDateField
              value={inicioActividades}
              onChange={onChangeInicio}
              emptyLabel="Elegir fecha"
              ariaLabel="Inicio de actividades"
              showClearButton={false}
              iosEmptyBehavior="openPicker"
            />
          </View>
        </View>
      </View>
      <ModalNestedPicker
        visible={iibbPickerVisible}
        title="Ingresos brutos"
        options={IIBB_PICKER_OPTIONS}
        selectedValue={iibbSelection}
        onSelect={(value) => {
          onChangeIibbSelection(value as IibbSelection);
          setIibbPickerVisible(false);
        }}
        onClose={() => setIibbPickerVisible(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    gap: spacing.md,
  },
  title: { ...typography.titleLg, color: ticketColors.ink },
  helper: { ...typography.caption, color: ticketColors.ink2 },
  grid: { gap: spacing.md },
  gridWide: { flexDirection: 'row', alignItems: 'flex-start' },
  field: { flex: 1, gap: spacing.sm, minWidth: 0 },
  label: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
  },
  input: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    color: ticketColors.ink,
    minHeight: 52,
  },
  select: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    backgroundColor: ticketColors.paper2,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: spacing.lg,
    minHeight: 52,
  },
  selectValue: { ...typography.body, color: ticketColors.ink, flex: 1 },
  disabled: { opacity: 0.55 },
});
